import { createBrowserRouter, Outlet, RouterProvider, ScrollRestoration, type RouteObject } from "react-router";
import { RequireAuth, RequireGuest } from "@/app/auth/guards";
import { guestAuthRoutes, openAuthRoutes } from "@/app/auth/routes";
import { NotFound, RouteErrorBoundary } from "@/app/errors";
import { AppLoading } from "@/app/shell/PageLoading";
import { featureRoutes, hasRouteFor, portalFeatureRoutes, publicFeatureRoutes } from "@/features/registry";
import { RealtimeProvider } from "@/lib/realtime";

/**
 * The route tree.
 *
 *   /login, /signup, …            guest-only auth screens
 *   /verify-email, /accept-invite auth screens that work signed in or out
 *   /p/:token, /i/:token, …       public links a client opens (no session)
 *   /portal/*                     client portal (agency-branded shell)
 *   everything else               the authenticated AppShell + feature routes
 *
 * Pages come from the feature registry and are code-split with react-router's
 * `lazy`, so the initial download is the shell and nothing else.
 */

/**
 * Areas owned by another team. They are mounted ONLY while no feature serves
 * them, so adding the real `activity` / `settings` feature removes the stand-in
 * without touching this file.
 */
const reservedRoutes: RouteObject[] = [
  ...(hasRouteFor("activity") ? [] : [{ path: "activity", lazy: () => import("@/app/core/activity") } satisfies RouteObject]),
  ...(hasRouteFor("settings") ? [] : [{ path: "settings/*", lazy: () => import("@/app/core/settings") } satisfies RouteObject]),
];

/** Providers that need the router (realtime toasts navigate) + scroll handling. */
function RootLayout() {
  return (
    <RealtimeProvider>
      <ScrollRestoration />
      <Outlet />
    </RealtimeProvider>
  );
}

export const routes: RouteObject[] = [
  {
    id: "root",
    element: <RootLayout />,
    ErrorBoundary: RouteErrorBoundary,
    HydrateFallback: AppLoading,
    children: [
      { element: <RequireGuest />, children: guestAuthRoutes },
      ...openAuthRoutes,
      ...publicFeatureRoutes,
      // Both shells are code-split: a signed-out visitor never downloads the app
      // chrome, and a portal contact never downloads the staff sidebar.
      { path: "portal", lazy: async () => ({ Component: (await import("@/app/shell/PortalShell")).PortalShell }), children: portalFeatureRoutes },
      {
        element: <RequireAuth />,
        children: [
          {
            lazy: async () => ({ Component: (await import("@/app/shell/AppShell")).AppShell }),
            children: [...featureRoutes, ...reservedRoutes],
          },
        ],
      },
      { path: "*", element: <NotFound /> },
    ],
  },
];

export const router = createBrowserRouter(routes);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
